import { confirmToast } from "../../utils/toast";
import React, { useEffect, useState } from "react";
import axios from "../../utils/axiosConfig";
import { useLookup } from "../../utils/useLookup";

const initialForm = {
  ShipperID: "",
  DealID: "",
  BuyerID: "",
  BuyerName: "",
  SCNo: "",
  NoOfContainer: "",
  Weight: "",
  CommissionType: "PerTone",  // PerTone | Lumpsum
  CommissionPct: "",
  IsPercent: false,
  Lumpsum: "",
  TotalCommission: "",
};

const Commission = () => {
  const { suppliers } = useLookup();
  const [activeTab, setActiveTab] = useState("list");
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [deals, setDeals] = useState([]);
  const [filterShipper, setFilterShipper] = useState("");
  const [filterDeal, setFilterDeal] = useState("");

  useEffect(() => { fetchList(); }, []);

  const fetchList = async (params = {}) => {
    try {
      const res = await axios.get("/commission/search", { params });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch { setRows([]); }
  };

  // When shipper changes in form, load deals for that shipper
  const handleShipperChange = async (shipperId) => {
    set("ShipperID", shipperId);
    set("DealID", "");
    set("BuyerID", "");
    set("BuyerName", "");
    set("SCNo", "");
    set("NoOfContainer", "");
    set("Weight", "");
    set("TotalCommission", "");
    if (!shipperId) { setDeals([]); return; }
    try {
      const res = await axios.get("/commission/deals", { params: { shipperId } });
      setDeals(Array.isArray(res.data) ? res.data : []);
    } catch { setDeals([]); }
  };

  // When deal changes, auto-fill buyer, SCNo, containers, weight
  const handleDealChange = async (dealId) => {
    set("DealID", dealId);
    set("BuyerID", "");
    set("BuyerName", "");
    set("SCNo", "");
    set("NoOfContainer", "");
    set("Weight", "");
    set("TotalCommission", "");
    if (!dealId) return;
    try {
      const res = await axios.get(`/commission/deal/${dealId}`);
      const d = res.data;
      setForm(f => ({
        ...f,
        DealID: dealId,
        BuyerID: d.BuyerID ?? "",
        BuyerName: d.BuyerName ?? "",
        SCNo: d.SCNo ?? "",
        NoOfContainer: d.NoOfContainer ?? "",
        Weight: d.TotalQty ?? "",
      }));
    } catch {}
  };

  // Recalculate total commission whenever relevant fields change
  const calcTotal = (f) => {
    const weight = parseFloat(f.Weight) || 0;
    if (f.CommissionType === "Lumpsum") {
      return parseFloat(f.Lumpsum) || 0;
    }
    // Per Tone
    const rate = parseFloat(f.CommissionPct) || 0;
    if (f.IsPercent) {
      // treat CommissionPct as a % of weight
      return parseFloat(((weight * rate) / 100).toFixed(2));
    }
    return parseFloat((weight * rate).toFixed(2));
  };

  const set = (k, v) => setForm(f => {
    const updated = { ...f, [k]: v };
    updated.TotalCommission = calcTotal(updated);
    return updated;
  });

  const handleSearch = () => fetchList({ shipperId: filterShipper, dealId: filterDeal });
  const handleClear  = () => { setFilterShipper(""); setFilterDeal(""); fetchList(); };

  const handleEdit = async (id) => {
    try {
      const res = await axios.get(`/commission/${id}`);
      const d = res.data;
      // also load deals for this shipper
      if (d.ShipperID) {
        const dr = await axios.get("/commission/deals", { params: { shipperId: d.ShipperID } });
        setDeals(Array.isArray(dr.data) ? dr.data : []);
      }
      setForm({
        ShipperID:       d.ShipperID ?? "",
        DealID:          d.DealID ?? "",
        BuyerID:         d.BuyerID ?? "",
        BuyerName:       d.BuyerName ?? "",
        SCNo:            d.SCNo ?? "",
        NoOfContainer:   d.NoOfContainer ?? "",
        Weight:          d.Weight ?? "",
        CommissionType:  d.CommissionType ?? "PerTone",
        CommissionPct:   d.CommissionPct ?? "",
        IsPercent:       !!d.IsPercent,
        Lumpsum:         d.Lumpsum ?? "",
        TotalCommission: d.TotalCommission ?? "",
      });
      setEditingId(id);
      setActiveTab("form");
    } catch { alert("Failed to load record."); }
  };

  const handleDelete = async (id) => {
    if (!await confirmToast("Delete this record?")) return;
    try {
      await axios.delete(`/commission/${id}`);
      fetchList();
    } catch { alert("Failed to delete."); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, TotalCommission: calcTotal(form) };
      if (editingId) {
        await axios.put(`/commission/${editingId}`, payload);
      } else {
        await axios.post("/commission", payload);
      }
      alert(editingId ? "Commission updated successfully" : "Commission saved successfully");
      resetForm();
      fetchList();
    } catch (err) {
      alert(err.response?.data?.message || err.response?.data || "Failed to save.");
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
    setDeals([]);
    setActiveTab("list");
  };

  const handleAddNew = () => {
    setForm(initialForm);
    setEditingId(null);
    setDeals([]);
    setActiveTab("form");
  };

  const fieldRow = (label, content) => (
    <div className="row mb-2 align-items-center" style={{ fontSize: "13px" }}>
      <label className="col-sm-3 text-end fw-semibold">{label}</label>
      <span className="col-auto px-1">:</span>
      <div className="col-sm-6">{content}</div>
    </div>
  );

  const row = (label, content, label2, content2) => (
    <>
      {fieldRow(label, content)}
      {label2 && fieldRow(label2, content2)}
    </>
  );

  return (
    <div className="container-fluid mt-3">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <span className="badge bg-success fs-6 px-3 py-2">Commission</span>
          {activeTab === "list"
            ? <button className="btn btn-success btn-sm" onClick={handleAddNew}>+ Add New</button>
            : <button className="btn btn-secondary btn-sm" onClick={resetForm}>Back to List</button>
          }
        </div>

        <div className="card-body">
          {activeTab === "list" ? (
            <>
              {/* Search */}
              <div className="border rounded p-3 mb-3 bg-light">
                <div className="row g-2 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label form-label-sm">Supplier</label>
                    <select className="form-select form-select-sm" value={filterShipper} onChange={e => setFilterShipper(e.target.value)}>
                      <option value="">All</option>
                      {suppliers.map(s => <option key={s.ShipperID} value={s.ShipperID}>{s.ShipperName}</option>)}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label form-label-sm">Deal ID</label>
                    <input type="number" className="form-control form-control-sm" value={filterDeal} onChange={e => setFilterDeal(e.target.value)} placeholder="Deal ID" />
                  </div>
                  <div className="col-auto d-flex gap-2">
                    <button className="btn btn-success btn-sm" onClick={handleSearch}>Search</button>
                    <button className="btn btn-secondary btn-sm" onClick={handleClear}>Clear</button>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div style={{ overflowX: "auto" }}>
                <table className="table table-bordered table-hover table-sm" style={{ fontSize: "13px" }}>
                  <thead className="table-success">
                    <tr>
                      <th>#</th><th>Supplier</th><th>Deal ID</th><th>S.C No</th>
                      <th>Buyer</th><th>Containers</th><th>Weight</th>
                      <th>Type</th><th>Commission</th><th>Total Commission</th>
                      <th>Action</th><th>Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0
                      ? <tr><td colSpan={12} className="text-center text-muted py-3">No records found.</td></tr>
                      : rows.map((r, i) => (
                        <tr key={r.CommissionID} style={{ background: i % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                          <td>{i + 1}</td>
                          <td>{r.ShipperName || "-"}</td>
                          <td>{r.DealID}</td>
                          <td>{r.SCNo || "-"}</td>
                          <td>{r.BuyerName || "-"}</td>
                          <td>{r.NoOfContainer ?? "-"}</td>
                          <td>{r.Weight ?? "-"}</td>
                          <td>{r.CommissionType}</td>
                          <td>{r.CommissionType === "Lumpsum" ? r.Lumpsum : `${r.CommissionPct}${r.IsPercent ? "%" : "/T"}`}</td>
                          <td>{r.TotalCommission}</td>
                          <td><span className="text-primary" style={{ cursor: "pointer" }} onClick={() => handleEdit(r.CommissionID)}>Edit</span></td>
                          <td><span className="text-danger" style={{ cursor: "pointer" }} onClick={() => handleDelete(r.CommissionID)}>Delete</span></td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            /* ── Form ── */
            <form onSubmit={handleSubmit}>
              <div className="border rounded p-3 bg-light" style={{ fontSize: "13px" }}>

                {/* Shipper Name */}
                {row(
                  "Shipper Name",
                  <select className="form-select form-select-sm" required value={form.ShipperID}
                    onChange={e => handleShipperChange(e.target.value)}>
                    <option value="">Select Supplier</option>
                    {suppliers.map(s => <option key={s.ShipperID} value={s.ShipperID}>{s.ShipperName}</option>)}
                  </select>
                )}

                {/* Select Deal + Buyer */}
                {row(
                  "Select Deal",
                  <div className="d-flex gap-1 align-items-center">
                    <select className="form-select form-select-sm" required value={form.DealID}
                      onChange={e => handleDealChange(e.target.value)}>
                      <option value="">Select Deal</option>
                      {deals.map(d => <option key={d.DealID} value={d.DealID}>{d.DealID}</option>)}
                    </select>
                    <span className="text-danger">*</span>
                  </div>,
                  "Buyer",
                  <input className="form-control form-control-sm" readOnly value={form.BuyerName} placeholder="Select Deal" />
                )}

                {/* S.C No + Weight */}
                {row(
                  "S.C No.",
                  <input className="form-control form-control-sm" readOnly value={form.SCNo} placeholder="Select Deal" />,
                  "Weight",
                  <input className="form-control form-control-sm" readOnly value={form.Weight} placeholder="Select Deal" />
                )}

                {/* No. of Container */}
                {row(
                  "No. of Container",
                  <input className="form-control form-control-sm" readOnly value={form.NoOfContainer} placeholder="Select Deal" />
                )}

                {/* Commission type */}
                <div className="row mb-2 align-items-start">
                  <label className="col-sm-3 text-end fw-semibold pt-1">Commission</label>
                  <span className="col-auto px-1 pt-1">:</span>
                  <div className="col-sm-6">
                    <div className="d-flex align-items-center gap-3 mb-1">
                      <div className="form-check mb-0">
                        <input className="form-check-input" type="radio" id="perTone"
                          checked={form.CommissionType === "PerTone"}
                          onChange={() => set("CommissionType", "PerTone")} />
                        <label className="form-check-label" htmlFor="perTone">Commission Per Tone</label>
                      </div>
                    </div>
                    {form.CommissionType === "PerTone" && (
                      <div className="d-flex align-items-center gap-2">
                        <input type="number" step="0.0001" className="form-control form-control-sm" style={{ width: 120 }}
                          value={form.CommissionPct}
                          onChange={e => set("CommissionPct", e.target.value)}
                          placeholder="Rate" />
                        <div className="form-check mb-0">
                          <input className="form-check-input" type="checkbox" id="isPercent"
                            checked={form.IsPercent}
                            onChange={e => set("IsPercent", e.target.checked)} />
                          <label className="form-check-label" htmlFor="isPercent">%</label>
                        </div>
                      </div>
                    )}
                    <div className="d-flex align-items-center gap-3 mt-2">
                      <div className="form-check mb-0">
                        <input className="form-check-input" type="radio" id="lumpsum"
                          checked={form.CommissionType === "Lumpsum"}
                          onChange={() => set("CommissionType", "Lumpsum")} />
                        <label className="form-check-label" htmlFor="lumpsum">Lumpsum</label>
                      </div>
                    </div>
                    {form.CommissionType === "Lumpsum" && (
                      <input type="number" step="0.01" className="form-control form-control-sm mt-1" style={{ width: 120 }}
                        value={form.Lumpsum}
                        onChange={e => set("Lumpsum", e.target.value)}
                        placeholder="Amount" />
                    )}
                  </div>
                </div>

                {/* Total Commission */}
                {row(
                  "Total Commission",
                  <input className="form-control form-control-sm" readOnly
                    value={form.TotalCommission !== "" ? form.TotalCommission : ""} />
                )}

                {/* Buttons */}
                <div className="d-flex gap-2 justify-content-center mt-3">
                  <button type="submit" className="btn btn-success btn-sm px-4">
                    {editingId ? "Update" : "Save"}
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm px-4" onClick={resetForm}>
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Commission;
